import { DotsVerticalIcon } from '@vizit/icons';
import { cva, cx } from '@vizit/theme';
import { HoverActionButton, HoverActionButtonHierarchy, HoverActionButtonSize } from '../hover-action-button';
import type { HoverActionButtonProps } from '../hover-action-button';
import { MenuTrigger } from '../menu';

interface ContextMenuProps extends React.ComponentPropsWithoutRef<typeof MenuTrigger> {
  /**
   * ref to be passed to MenuPopover.
   *
   * @optional
   * @type boolean
   * @default false
   */
  ref?: React.RefObject<HTMLDivElement>;
  /**
   * Props to be passed to the trigger button (a HoverActionButton). The
   * trigger is visible while hovered or while the menu is open.
   *
   * @optional
   * @type {isHovered: boolean, isMenuOpen: boolean} & Partial<HoverActionButtonProps>
   * @default {}
   */
  triggerProps?: {
    isHovered?: boolean;
    isMenuOpen?: boolean;
  } & Partial<Omit<HoverActionButtonProps, 'icon'>> & { icon?: React.ReactNode };
}

const triggerVisibilityClasses = cva({
  // Keyboard users reach the trigger without hovering; reveal it on focus so
  // it is never an invisible tab stop, whatever the consumer passes.
  base: 'focus-visible:opacity-100',
  variants: {
    isVisible: {
      true: 'opacity-100',
      false: 'opacity-0',
    },
  },
  defaultVariants: {
    isVisible: false,
  },
});

function ContextMenu(props: ContextMenuProps) {
  const { children, triggerProps = {}, ...other } = props;
  const {
    isHovered,
    isMenuOpen,
    icon,
    hierarchy = HoverActionButtonHierarchy.Secondary,
    size = HoverActionButtonSize.Sm,
    className,
    'aria-label': ariaLabel = 'Open menu',
    ...otherTriggerProps
  } = triggerProps;

  return (
    <MenuTrigger {...other}>
      <HoverActionButton
        aria-label={ariaLabel}
        hierarchy={hierarchy}
        size={size}
        icon={icon ?? <DotsVerticalIcon size={20} />}
        className={cx(
          size === HoverActionButtonSize.Sm && 'h-9 w-9 sm:h-18 sm:w-18',
          triggerVisibilityClasses({ isVisible: isHovered || isMenuOpen }),
          className,
        )}
        {...otherTriggerProps}
      />
      {children}
    </MenuTrigger>
  );
}

export { ContextMenu };
